import _ from 'lodash';
import { t, Trans } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_BREAKOUT_CHART_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import FixedLabelContainer, { Row, Label, Content, Line } from '../../fixed-label-container/FixedLabelContainer';
import StatusBreakout from '../../charts/status-breakout/StatusBreakout';
import StatusBreakoutRow from '../../status-breakout/StatusBreakoutRow';
import { convertStatusToStatusCounts } from '../../charts/data-series';
import {
    useWirelessController,
    networkDeviceFilterId,
    wirelessControllerFilterId,
    timeRangeFilterId,
} from '../../filters';
import usePanelDataService from '../../../services/PanelDataService';

const title = t`Wireless Controller Status`;

const empty = { trends: { status: { data: [] } } };

const dataSource = {
    url: '/query/table',
    params: {
        type: 'device',
        metrics: ['status', 'deviceInterfaceStatus', 'deviceInterfaceCounts'],
        trends: ['status', 'deviceInterfaceCounts'],
        renderAsEntity: true,
    },
};

function checkData(data) {
    return _.get(data, 'deviceInterfaceCounts.total') > 0;
}

function WirelessControllerStatusBreakoutPanel() {
    const wlc = useWirelessController();
    const statusCounts = usePanelDataService(dataSource);
    const statusData = statusCounts?.data?.[0];

    // We need to pass a Device filter (not WirelessController) for the target page on the status breakout link
    const filters = [{ name: networkDeviceFilterId, uuid: wlc?.uuid }];

    if (!wlc || !statusCounts) return null;

    const overAllCounts = statusData ? convertStatusToStatusCounts('status', statusData) : empty;
    const hasInterfaces = checkData(statusData);

    return (
        <FixedLabelContainer>
            <Row>
                <Label>
                    <Trans>Overall Status</Trans>
                </Label>
                <Content>
                    <StatusBreakout
                        height={20}
                        noSelection
                        disableTimeAxis
                        metricName="status"
                        data={overAllCounts}
                        isStatus
                    />
                </Content>
            </Row>
            {hasInterfaces && (
                <>
                    <Line />
                    <Row>
                        <StatusBreakoutRow
                            noSelection
                            disableSelection
                            page="/interfaces"
                            title={<Trans>Interfaces</Trans>}
                            metric="deviceInterfaceCounts"
                            last="deviceInterfaceCounts"
                            data={statusData}
                            filters={filters}
                            excludedFilters={[wirelessControllerFilterId]}
                            hideProblems
                        />
                    </Row>
                </>
            )}
        </FixedLabelContainer>
    );
}

WirelessControllerStatusBreakoutPanel.propTypes = panelPropTypes;

WirelessControllerStatusBreakoutPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 50,
};

export default Panel({
    typeAlias: 'WirelessControllerStatusBreakout',
    catalogEntry: {
        category: PANEL_CATEGORIES.WIRELESS_CONTROLLER,
        priority: PANEL_PRIORITIES.STATUS,
        title,
        description: t`Status breakdown for a selected wireless controller`,
        previewImage: STATUS_BREAKOUT_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, wirelessControllerFilterId],
        optional: [],
    },
})(WirelessControllerStatusBreakoutPanel);
